/**
 * VUKZ Assist — Realtime (WebRTC) voice client. RT2.
 *
 * Authority split (non-negotiable):
 *   - Realtime  = conversation (listen / speak / barge-in). NOTHING else.
 *   - Assist    = business READ authority (via backend POST /realtime/turn).
 *   - User/UI   = business WRITE authority (CTAs, Aplicar, etc).
 *
 * The model only has ONE tool: `vukz_assist_turn(text)`. Every function call
 * is forwarded to `onToolCall(text, meta)` (provided by the panel), which
 * runs the canonical Assist pipeline and returns `{ speech_text }`. The
 * client then sends a MINIMAL function_call_output + response.create.
 *
 * This module is framework-agnostic (no React) so it can be unit-tested
 * with mocked RTCPeerConnection / getUserMedia / fetch.
 */

export const REALTIME_TOOL_NAME = 'vukz_assist_turn';

export const RealtimeStatus = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  LISTENING: 'listening',
  THINKING: 'thinking',
  SPEAKING: 'speaking',
  ERROR: 'error',
});

const noop = () => {};

/** Safe JSON.parse — returns null on invalid input (server events, tool args). */
export const safeParse = (raw) => {
  if (typeof raw !== 'string') return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
};

/** Extract the `text` argument of a vukz_assist_turn call (or null). */
export const parseToolArgs = (argsRaw) => {
  const args = safeParse(argsRaw);
  const text = args && typeof args.text === 'string' ? args.text.trim() : '';
  return text ? text : null;
};

/**
 * RT4.0 — latências derivadas dos marcos monotônicos de um turno (ms).
 * Marcos (performance.now()): speech_started, speech_stopped, fc_args_done,
 * turn_sent, turn_received, fc_output_sent, response_created, first_audio,
 * playback_playing, response_done. Qualquer marco ausente → métrica null.
 *
 * first_audio (RT4.0-FIX): em WebRTC o áudio vai pela media track, não por
 * `response.output_audio.delta`; o sinal por turno é `output_audio_buffer.started`
 * (servidor começou a enviar áudio). `output_audio.delta` fica como fallback.
 * playback_playing: `<audio>.onplaying` — dispara quando o elemento (re)inicia
 * reprodução; na track contínua WebRTC isso ocorre no connect, não por turno,
 * por isso é AUXILIAR.
 */
export const computeTurnLatencies = (marks = {}) => {
  const d = (a, b) => (Number.isFinite(marks[a]) && Number.isFinite(marks[b]))
    ? Math.round(marks[b] - marks[a]) : null;
  return {
    speech_to_fc_ms: d('speech_stopped', 'fc_args_done'),        // fim da fala → tool call
    fc_to_turn_sent_ms: d('fc_args_done', 'turn_sent'),
    turn_roundtrip_ms: d('turn_sent', 'turn_received'),          // /realtime/turn (rede + backend)
    fc_to_turn_response_ms: d('fc_args_done', 'turn_received'),  // tool call → resposta do /turn
    output_to_first_audio_ms: d('fc_output_sent', 'first_audio'),// function output → áudio começou (server)
    output_to_playback_ms: d('fc_output_sent', 'playback_playing'), // aux (browser onplaying, se houver)
    output_to_response_created_ms: d('fc_output_sent', 'response_created'),
    first_audio_to_done_ms: d('first_audio', 'response_done'),
    total_perceived_ms: d('speech_stopped', 'first_audio'),      // latência percebida
    total_turn_ms: d('speech_stopped', 'response_done'),
  };
};

/**
 * RT4.1 — erro do servidor é benigno SÓ se correlacionado (event_id) a um
 * comando nosso de barge-in E for condição conhecida da corrida com o
 * server_vad (resposta já encerrada / buffer já limpo / item já truncado ou
 * inexistente). Qualquer outra coisa continua terminal.
 */
/** Janela (ms) após nosso response.cancel em que response_cancel_not_active sem event_id ecoado ainda é benigno. */
export const BARGE_IN_ERROR_WINDOW_MS = 2000;

export const isBenignBargeInError = (cmdType, error) => {
  const code = String(error?.code || '').toLowerCase();
  const msg = String(error?.message || '').toLowerCase();
  const has = (...needles) => needles.some((n) => code.includes(n) || msg.includes(n));
  switch (cmdType) {
    case 'response.cancel':
      return has('no active response', 'response_cancel_not_active', 'not_active', 'already', 'no response');
    case 'output_audio_buffer.clear':
      return has('no active response', 'not_active', 'already', 'empty', 'no audio');
    case 'conversation.item.truncate':
      return has('already', 'truncat', 'not found', 'does not exist', 'item_not_found', 'invalid_item', 'audio_end_ms');
    default:
      return false;
  }
};

export class RealtimeClient {
  /**
   * @param {Object} opts
   * @param {() => Promise<Object>} opts.createSession — returns backend session
   *        { client_secret, calls_url, model, voice, language, tool_name }
   * @param {(text: string, meta: Object) => Promise<{speech_text?: string}>} opts.onToolCall
   * @param {(status: string, detail?: Object) => void} [opts.onStatus]
   * @param {(t: {role: string, text: string, final: boolean}) => void} [opts.onTranscript]
   * @param {(err: Error) => void} [opts.onError]
   * @param {HTMLAudioElement} [opts.audioElement] — remote audio sink (autoplay)
   * @param {Object} [opts.deps] — injectable for tests
   */
  constructor({
    createSession,
    onToolCall,
    onStatus = noop,
    onTranscript = noop,
    onError = noop,
    onEvent = noop,
    onTiming = noop,
    onBargeIn = noop,
    audioElement = null,
    deps = {},
  }) {
    if (typeof createSession !== 'function') throw new Error('createSession is required');
    if (typeof onToolCall !== 'function') throw new Error('onToolCall is required');
    this.createSession = createSession;
    this.onToolCall = onToolCall;
    this.onStatus = onStatus;
    this.onTranscript = onTranscript;
    this.onError = onError;
    // Debug/observer hook: recebe (dir, {type, call_id, response_id}) — NUNCA
    // o payload bruto (sem ek_, sem áudio, sem args).
    this.onEvent = onEvent;
    // RT4.0: recebe {call_id, marks, latencies, server_timings} ao fim de
    // cada turno com tool call. Observabilidade — não altera comportamento.
    this.onTiming = onTiming;
    // RT4.1: recebe {response_id, item_id, content_index, audio_end_ms,
    // audio_end_ms_raw, truncated, marks} a cada barge-in executado.
    this.onBargeIn = onBargeIn;
    this.audioElement = audioElement;

    this.deps = {
      RTCPeerConnection: deps.RTCPeerConnection
        || (typeof window !== 'undefined' ? window.RTCPeerConnection : undefined),
      getUserMedia: deps.getUserMedia
        || ((c) => navigator.mediaDevices.getUserMedia(c)),
      fetch: deps.fetch || ((...a) => fetch(...a)),
      // Relógio MONOTÔNICO (performance.now) — nunca Date.now para latência.
      now: deps.now || ((typeof performance !== 'undefined' && performance.now)
        ? () => performance.now() : () => Date.now()),
    };

    this.pc = null;
    this.dc = null;
    this.micStream = null;
    this.session = null;
    this.status = RealtimeStatus.IDLE;
    this.processedCallIds = new Set();
    this.closed = false;
    // RT2-FIX1: 'disconnected' é transitório em WebRTC — só 'failed' é terminal.
    this.disconnectGraceMs = Number.isFinite(deps.disconnectGraceMs) ? deps.disconnectGraceMs : 9000;
    this.disconnectTimer = null;
    this.statusBeforeDisconnect = null;
    // RT4.0: turno corrente (marcos) — 1 turno = fala do usuário → tool call
    // → /turn → function_call_output → resposta falada (response.done).
    this.turn = null;
    // RT4.0-FIX: identificação da sessão local (não é o id do servidor) e
    // índice de turno, para que cada timing seja atribuível sem adivinhar.
    this.sessionId = null;
    this.turnIndex = 0;
    // RT4.1 — resposta em reprodução (para conversation.item.truncate):
    //   playing = { response_id, item_id, content_index, first_audio }
    // content_index NÃO é assumido 0: confirmado em response.content_part.added
    // (part.type === 'output_audio' | 'audio'). Barge-in idempotente por
    // response_id (server_vad pode auto-limpar antes do nosso clear).
    this.playing = null;
    this.interruptedResponseIds = new Set();
    // event_ids dos nossos comandos de barge-in → classificação de erro correlacionado
    this.bargeInEventIds = new Map(); // event_id → 'response.cancel' | 'output_audio_buffer.clear' | 'conversation.item.truncate'
    this.lastBargeInAt = null; // RT4.1: âncora da janela de erro benigno contextual
    this.eventSeq = 0;
    // VOICE-FINAL (V5/V6): turno de ferramenta em voo. Barge-in enquanto o
    // /turn ainda não respondeu → aborta o HTTP e marca a geração como
    // cancelada: o resultado tardio NÃO vira fala nem ação; o function_call
    // recebe status 'cancelled' (o modelo ignora em silêncio).
    this.inflight = null; // { callId, controller, gen }
    this.turnGen = 0;
    this.muted = false;
  }

  _nextEventId(prefix) {
    this.eventSeq += 1;
    return `${prefix}_${this.sessionId || 'nosess'}_${this.eventSeq}`;
  }

  // ── RT4.0 timing helpers (sem efeito no comportamento) ──────────
  _now() { try { return this.deps.now(); } catch { return Date.now(); } }

  _newSessionId() {
    const rnd = Math.random().toString(36).slice(2, 8);
    return `rts_${Date.now().toString(36)}_${rnd}`;
  }

  _turnBegin() {
    this.turnIndex += 1;
    this.turn = { call_id: null, marks: {}, server_timings: null, seq: this.turnIndex };
    return this.turn;
  }

  /** Marca uma vez (o primeiro valor vence — ex.: first_audio). */
  _mark(name, opts = {}) {
    if (!this.turn) { if (!opts.begin) return; this._turnBegin(); }
    if (opts.begin && this.turn.marks.response_done != null) this._turnBegin();
    if (this.turn.marks[name] == null) this.turn.marks[name] = this._now();
  }

  _turnFinish() {
    const t = this.turn;
    if (!t) return;
    this.turn = null;
    const report = {
      session_id: this.sessionId,
      turn_index: t.seq,
      call_id: t.call_id,
      seq: t.seq,
      marks: { ...t.marks },
      latencies: computeTurnLatencies(t.marks),
      server_timings: t.server_timings || null,
    };
    try { this.onTiming(report); } catch { /* ignore listener errors */ }
  }

  _setStatus(status, detail) {
    if (this.closed && status !== RealtimeStatus.IDLE) return;
    this.status = status;
    try { this.onStatus(status, detail); } catch { /* ignore listener errors */ }
  }

  _fail(err) {
    const e = err instanceof Error ? err : new Error(String(err));
    this._setStatus(RealtimeStatus.ERROR, { message: e.message });
    try { this.onError(e); } catch { /* ignore */ }
  }

  /** Full connect: session → pc → mic → data channel → SDP exchange. */
  async connect() {
    if (this.pc) return; // already connected/connecting
    this.closed = false;
    this.sessionId = this._newSessionId();
    this.turnIndex = 0;
    this.turn = null;
    this.playing = null;
    this.interruptedResponseIds = new Set();
    this.bargeInEventIds = new Map();
    this.lastBargeInAt = null;
    this._setStatus(RealtimeStatus.CONNECTING);
    try {
      this.session = await this.createSession();
      if (!this.session?.client_secret || !this.session?.calls_url) {
        throw new Error('realtime_session_invalid');
      }
      this.createPeerConnection();
      await this.attachMicrophone();
      this.openDataChannel();
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      const answerSdp = await this.postSDP(offer.sdp);
      await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (err) {
      this._fail(err);
      this.close();
      throw err;
    }
  }

  createPeerConnection() {
    const { RTCPeerConnection: PC } = this.deps;
    if (!PC) throw new Error('webrtc_unsupported');
    this.pc = new PC();
    this.pc.ontrack = (ev) => {
      const stream = ev.streams?.[0];
      if (this.audioElement && stream) {
        this.audioElement.srcObject = stream;
        // RT4.0-FIX: marco auxiliar de playback no browser. Na track contínua
        // WebRTC dispara no início da sessão (não por turno) — por isso aux.
        this.audioElement.onplaying = () => {
          if (this.turn?.marks.fc_output_sent != null) this._mark('playback_playing');
          this._emit('local', { type: 'audio.playing' });
        };
        const p = this.audioElement.play?.();
        if (p && typeof p.catch === 'function') p.catch(noop);
      }
    };
    this.pc.onconnectionstatechange = () => this._onConnectionState(this.pc?.connectionState);
    return this.pc;
  }

  _clearDisconnectTimer() {
    if (this.disconnectTimer) { clearTimeout(this.disconnectTimer); this.disconnectTimer = null; }
  }

  /**
   * Política RT2-FIX1:
   *   connected     → normal (cancela grace; retoma status anterior)
   *   disconnected  → NÃO é erro; aguarda `disconnectGraceMs`; se continuar
   *                   disconnected → peer_disconnected_timeout (fatal)
   *   failed        → fatal (peer_failed)
   *   closed        → fatal só se NÃO fomos nós que fechamos (peer_closed)
   * Sem restartIce() no piloto.
   */
  _onConnectionState(state) {
    if (this.closed) return;
    if (state === 'connected') {
      if (this.disconnectTimer) {
        this._clearDisconnectTimer();
        this._setStatus(this.statusBeforeDisconnect || RealtimeStatus.LISTENING, { recovered: true });
        this.statusBeforeDisconnect = null;
      }
      return;
    }
    if (state === 'disconnected') {
      if (!this.disconnectTimer) {
        this.statusBeforeDisconnect = this.status;
        this._setStatus(RealtimeStatus.CONNECTING, { reconnecting: true });
        this.disconnectTimer = setTimeout(() => {
          this.disconnectTimer = null;
          if (!this.closed && this.pc?.connectionState === 'disconnected') {
            this._fail(new Error('peer_disconnected_timeout'));
          }
        }, this.disconnectGraceMs);
      }
      return;
    }
    if (state === 'failed') {
      this._clearDisconnectTimer();
      this._fail(new Error('peer_failed'));
      return;
    }
    if (state === 'closed') {
      this._clearDisconnectTimer();
      this._fail(new Error('peer_closed'));
    }
  }

  async attachMicrophone() {
    this.micStream = await this.deps.getUserMedia({ audio: true });
    this.micStream.getTracks().forEach((t) => this.pc.addTrack(t, this.micStream));
    return this.micStream;
  }

  openDataChannel() {
    this.dc = this.pc.createDataChannel('oai-events');
    this.dc.onopen = () => this._setStatus(RealtimeStatus.LISTENING);
    this.dc.onmessage = (ev) => this.handleServerEvent(ev.data);
    this.dc.onerror = (ev) => this._fail(new Error(ev?.error?.message || 'datachannel_error'));
    return this.dc;
  }

  async postSDP(sdp) {
    const res = await this.deps.fetch(this.session.calls_url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.session.client_secret}`,
        'Content-Type': 'application/sdp',
      },
      body: sdp,
    });
    if (!res.ok) throw new Error(`realtime_calls_${res.status}`);
    return res.text();
  }

  _emit(dir, evt) {
    try {
      this.onEvent(dir, {
        type: evt?.type,
        call_id: evt?.call_id || evt?.item?.call_id || undefined,
        response_id: evt?.response_id || evt?.response?.id || undefined,
        item_type: evt?.item?.type || undefined,
        item_id: evt?.item_id || evt?.item?.id || undefined,
        event_id: evt?.event_id || undefined,
        content_index: (typeof evt?.content_index === 'number') ? evt.content_index : undefined,
        audio_end_ms: (typeof evt?.audio_end_ms === 'number') ? evt.audio_end_ms : undefined,
        error_code: evt?.error?.code || undefined,
        // RT4.1: id do comando NOSSO que causou o erro (o servidor ecoa em error.event_id)
        client_event_id: evt?.error?.event_id || evt?.client_event_id || undefined,
        correlation: evt?.correlation || undefined,
        ms: this._now(), // RT4.0: relógio monotônico para linha do tempo
      });
    } catch { /* ignore */ }
  }

  _send(evt) {
    if (!this.dc || this.dc.readyState !== 'open') return false;
    this.dc.send(JSON.stringify(evt));
    this._emit('out', evt);
    return true;
  }

  /** Server → client events. Only conversation plumbing; business = Assist. */
  handleServerEvent(raw) {
    const evt = safeParse(raw);
    if (!evt || typeof evt.type !== 'string') return;
    this._emit('in', evt);
    switch (evt.type) {
      case 'input_audio_buffer.speech_started':
        this._mark('speech_started', { begin: true });
        // Barge-in: user started talking — stop whatever is playing (RT4.1:
        // cancel → clear → truncate, idempotente por response_id).
        if (this.status === RealtimeStatus.SPEAKING || this.playing) this.bargeIn(this._now());
        // VOICE-FINAL (V6): se um /turn ainda está em voo, o usuário falou por
        // cima do "pensando" — cancela para que a resposta antiga nunca fale
        // nem execute ação depois da nova fala.
        this.cancelInflightTurn('speech_started');
        this._setStatus(RealtimeStatus.LISTENING);
        break;
      case 'input_audio_buffer.speech_stopped':
        this._mark('speech_stopped', { begin: true });
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (evt.transcript) this.onTranscript({ role: 'user', text: evt.transcript, final: true });
        break;
      case 'response.created':
        // Só interessa a resposta FALADA (após o function_call_output).
        if (this.turn?.marks.fc_output_sent != null) this._mark('response_created');
        break;
      case 'response.output_item.added':
        // RT4.1: item de mensagem do assistente = candidato a truncate.
        if (evt.item?.type === 'message' && evt.item?.role === 'assistant' && evt.item?.id) {
          this.playing = {
            response_id: evt.response_id || null,
            item_id: evt.item.id,
            content_index: null, // confirmado em content_part.added (áudio)
            first_audio: null,
          };
        }
        break;
      case 'response.content_part.added':
        if (this.playing && evt.item_id === this.playing.item_id
            && (evt.part?.type === 'output_audio' || evt.part?.type === 'audio')
            && typeof evt.content_index === 'number' && this.playing.content_index == null) {
          this.playing.content_index = evt.content_index;
        }
        break;
      case 'output_audio_buffer.started':
        // WebRTC: servidor começou a enviar áudio pela media track → first_audio.
        this._localResume();
        if (this.turn?.marks.fc_output_sent != null) this._mark('first_audio');
        if (this.playing && this.playing.first_audio == null) {
          if (!this.playing.response_id && evt.response_id) this.playing.response_id = evt.response_id;
          this.playing.first_audio = this._now();
        }
        if (this.status !== RealtimeStatus.SPEAKING) this._setStatus(RealtimeStatus.SPEAKING);
        break;
      case 'response.output_audio.delta':
      case 'response.audio.delta':
        // Fallback (modos sem media track). first_audio: o primeiro vence.
        if (this.turn?.marks.fc_output_sent != null) this._mark('first_audio');
        if (this.playing && this.playing.first_audio == null && (!evt.item_id || evt.item_id === this.playing.item_id)) {
          this.playing.first_audio = this._now();
        }
        if (this.status !== RealtimeStatus.SPEAKING) this._setStatus(RealtimeStatus.SPEAKING);
        break;
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        // Fim natural ou auto-clear do servidor (server_vad). Não zera `playing`
        // aqui: o truncate ainda pode ser necessário até response.done.
        break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        if (evt.transcript) this.onTranscript({ role: 'assistant', text: evt.transcript, final: true });
        break;
      case 'response.function_call_arguments.done':
        this._mark('fc_args_done', { begin: true });
        this._handleFunctionCall(evt);
        break;
      case 'response.done': {
        // Model may also emit function_call as an output item.
        const items = evt.response?.output || [];
        const fcItems = items.filter((it) => it?.type === 'function_call');
        if (fcItems.length) this._mark('fc_args_done', { begin: true });
        fcItems.forEach((it) => this._handleFunctionCall(it));
        if (this.status !== RealtimeStatus.THINKING) this._setStatus(RealtimeStatus.LISTENING);
        // Fecha o turno só na resposta falada (depois do function_call_output).
        if (this.turn?.marks.fc_output_sent != null && !fcItems.length) {
          this._mark('response_done');
          this._turnFinish();
        }
        // RT4.1: resposta encerrada (completa ou cancelada) → nada mais em reprodução.
        const rid = evt.response?.id;
        if (this.playing && (!rid || !this.playing.response_id || this.playing.response_id === rid)) this.playing = null;
        break;
      }
      case 'error': {
        // RT4.1: só é benigno se CORRELACIONADO a um comando nosso de barge-in
        // E for condição conhecida. Qualquer outro erro continua terminal.
        // Correlação primária: o servidor ecoa o event_id do comando cliente em
        // error.event_id (evt.event_id é o id do PRÓPRIO evento de erro).
        const clientId = evt.error?.event_id || undefined;
        let cmd = clientId ? this.bargeInEventIds.get(clientId) : undefined;
        let how = cmd ? 'event_id' : null;
        if (cmd) this.bargeInEventIds.delete(clientId);
        // Fallback contextual (janela curta): server_vad com interrupt_response
        // pode cancelar a resposta ANTES do nosso response.cancel chegar; alguns
        // servidores não ecoam event_id. Benigno só se: houve barge-in nosso há
        // < BARGE_IN_ERROR_WINDOW_MS E o código é o da corrida conhecida.
        if (!cmd && this.lastBargeInAt != null
            && (this._now() - this.lastBargeInAt) <= BARGE_IN_ERROR_WINDOW_MS) {
          const code = String(evt.error?.code || '').toLowerCase();
          if (code === 'response_cancel_not_active') { cmd = 'response.cancel'; how = 'window'; }
        }
        if (cmd && isBenignBargeInError(cmd, evt.error)) {
          this._emit('local', {
            type: 'barge_in.benign_error', event_id: evt.event_id, client_event_id: clientId,
            correlation: how, error: { code: evt.error?.code },
          });
          break;
        }
        this._fail(new Error(evt.error?.message || 'realtime_error'));
        break;
      }
      default:
        break;
    }
  }

  async _handleFunctionCall(evt) {
    if (evt?.name !== REALTIME_TOOL_NAME) return; // only our single tool
    const callId = evt.call_id || evt.id;
    if (!callId || this.processedCallIds.has(callId)) return; // idempotent
    this.processedCallIds.add(callId);
    if (this.turn) this.turn.call_id = callId;
    const text = parseToolArgs(evt.arguments);
    if (!text) {
      this.sendFunctionCallOutput(callId, { status: 'error', speech_text: 'Não entendi. Pode repetir?' });
      return;
    }
    this._setStatus(RealtimeStatus.THINKING, { text });
    // VOICE-FINAL-INTEGRATION-1 (I7): a chegada de OUTRA function_call NÃO é
    // evidência de um novo turno do usuário — só speech_started/barge-in
    // cancela (cancelInflightTurn). Mesmo texto em voo → junta (dedup: a
    // mesma saída serve às duas chamadas, zero execução nova). Texto
    // diferente → serializa depois da em voo (nunca duas execuções vivas,
    // nunca abortar o que o usuário pediu).
    if (this.inflight) {
      const prev = this.inflight;
      if (prev.text === text) {
        this._emit('local', { type: 'turn.joined', call_id: callId, joined_to: prev.callId });
        let out = null;
        try { out = await prev.promise; } catch { out = null; }
        if (this.closed) return;
        // Só o item de saída: a response falada já foi criada pela chamada
        // original (um segundo response.create seria erro terminal).
        this.sendFunctionCallOutput(callId, out || { speech_text: '', status: 'cancelled' }, { createResponse: false });
        return;
      }
      this._emit('local', { type: 'turn.queued', call_id: callId, after: prev.callId });
      try { await prev.promise; } catch { /* ignore */ }
      if (this.closed) return;
    }
    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const gen = ++this.turnGen;
    const inf = { callId, text, controller, gen, promise: null };
    this.inflight = inf;
    inf.promise = this._executeTurn(inf);
    await inf.promise;
  }

  /** Executa UM turno de ferramenta. Resolve com a saída enviada ao modelo
   *  ({status, speech_text}) ou null quando cancelado (nada enviado aqui). */
  async _executeTurn(inf) {
    const { callId, text, controller, gen } = inf;
    const stale = () => this.closed || !this.inflight || this.inflight.gen !== gen;
    try {
      this._mark('turn_sent');
      const result = await this.onToolCall(text, { callId, signal: controller?.signal, isCancelled: stale });
      this._mark('turn_received');
      if (stale()) {
        // Cancelado por barge-in enquanto em voo: nada é falado, nenhuma ação.
        this._emit('local', { type: 'turn.cancelled_late', call_id: callId });
        return null;
      }
      this.inflight = null;
      if (this.turn && result && result.timings && typeof result.timings === 'object') {
        this.turn.server_timings = result.timings; // meta.timings do /realtime/turn
      }
      let out;
      if (result == null) {
        // VOICE-FINAL (V19): o painel recusou o turno (ex.: outro em andamento)
        // — não fingir sucesso com "Pronto.".
        out = { status: 'error', speech_text: 'Não consegui processar agora. Pode repetir?' };
      } else {
        const speech = (typeof result.speech_text === 'string' && result.speech_text.trim())
          ? result.speech_text.trim()
          : 'Pronto.';
        out = { status: 'ok', speech_text: speech };
      }
      this.sendFunctionCallOutput(callId, out);
      return out;
    } catch (err) {
      this._mark('turn_received');
      if (stale() || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        this._emit('local', { type: 'turn.cancelled_late', call_id: callId });
        return null;
      }
      this.inflight = null;
      const out = {
        status: 'error',
        speech_text: 'Não consegui consultar agora. Tente novamente em instantes.',
      };
      this.sendFunctionCallOutput(callId, out);
      try { this.onError(err instanceof Error ? err : new Error(String(err))); } catch { /* ignore */ }
      return out;
    }
  }

  /**
   * VOICE-FINAL (V6) — cancela o turno de ferramenta em voo (se houver):
   * aborta o HTTP, invalida a geração e fecha o function_call com status
   * 'cancelled' (sem fala) para o modelo não ficar com a tool pendurada.
   * Idempotente. Nunca lança.
   */
  cancelInflightTurn(reason = 'barge_in') {
    const inf = this.inflight;
    if (!inf) return false;
    this.inflight = null;
    this.turnGen += 1; // invalida a geração em voo
    try { inf.controller?.abort(); } catch { /* ignore */ }
    this._send({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: inf.callId,
        output: JSON.stringify({ speech_text: '', status: 'cancelled' }) },
    });
    this._emit('local', { type: 'turn.cancelled', call_id: inf.callId, reason, ms: this._now() });
    if (this.turn) { this.turn.cancelled = true; }
    return true;
  }

  /** VOICE-FINAL (V18) — mute: desliga a track do microfone (nenhum áudio sobe). */
  setMuted(muted) {
    this.muted = !!muted;
    try { this.micStream?.getAudioTracks().forEach((t) => { t.enabled = !this.muted; }); } catch { /* ignore */ }
    this._emit('local', { type: this.muted ? 'mic.muted' : 'mic.unmuted' });
    return this.muted;
  }

  /** Minimal output ONLY (speech_text + status). Never the full Assist payload. */
  sendFunctionCallOutput(callId, output, { createResponse = true } = {}) {
    const minimal = { speech_text: String(output?.speech_text || ''), status: output?.status || 'ok' };
    this._send({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(minimal) },
    });
    if (!createResponse) return;
    this._mark('fc_output_sent');
    this._send({ type: 'response.create' });
  }

  _sendBargeInCmd(evt) {
    const event_id = this._nextEventId(evt.type === 'response.cancel' ? 'bi_cancel'
      : evt.type === 'output_audio_buffer.clear' ? 'bi_clear' : 'bi_trunc');
    this.bargeInEventIds.set(event_id, evt.type);
    if (evt.type === 'response.cancel') this.lastBargeInAt = this._now();
    // Higiene: não deixa o mapa crescer indefinidamente.
    if (this.bargeInEventIds.size > 60) {
      const first = this.bargeInEventIds.keys().next().value;
      this.bargeInEventIds.delete(first);
    }
    return this._send({ event_id, ...evt });
  }

  /**
   * RT4.1 — Barge-in idempotente por response_id:
   *   response.cancel → output_audio_buffer.clear → conversation.item.truncate
   * audio_end_ms ≈ quanto o usuário realmente ouviu = agora − first_audio do
   * item (âncora: output_audio_buffer.started, relógio monotônico), ≥ 0.
   * Sem item/âncora/content_index confirmado → cancel+clear, truncate pulado
   * (registrado em telemetria). Nunca lança.
   */
  bargeIn(atMs = this._now()) {
    const p = this.playing;
    if (!p && this.status !== RealtimeStatus.SPEAKING) {
      this._emit('local', { type: 'barge_in.nothing_playing' });
      return null;
    }
    const rid = p?.response_id || null;
    if (rid && this.interruptedResponseIds.has(rid)) {
      this._emit('local', { type: 'barge_in.duplicate_ignored', response_id: rid });
      return null;
    }
    if (rid) this.interruptedResponseIds.add(rid);
    if (this.interruptedResponseIds.size > 100) {
      const first = this.interruptedResponseIds.values().next().value;
      this.interruptedResponseIds.delete(first);
    }

    // VOICE-FINAL (V5): silêncio local IMEDIATO — não espera o clear do
    // servidor propagar pela media track. O elemento volta a tocar no
    // próximo output_audio_buffer.started.
    this._localSilence();

    this._sendBargeInCmd({ type: 'response.cancel' });
    this._sendBargeInCmd({ type: 'output_audio_buffer.clear' });

    let truncated = false;
    let audioEndRaw = null;
    let audioEnd = null;
    let skipReason = null;
    if (!p || !p.item_id) skipReason = 'no_playing_item';
    else if (p.first_audio == null) skipReason = 'no_first_audio';
    else if (p.content_index == null) skipReason = 'no_audio_content_index';
    if (!skipReason) {
      audioEndRaw = Math.round(atMs - p.first_audio);
      audioEnd = Math.max(0, audioEndRaw);
      truncated = this._sendBargeInCmd({
        type: 'conversation.item.truncate',
        item_id: p.item_id,
        content_index: p.content_index,
        audio_end_ms: audioEnd,
      });
    }
    const report = {
      session_id: this.sessionId,
      response_id: rid,
      item_id: p?.item_id || null,
      content_index: p?.content_index ?? null,
      audio_end_ms_raw: audioEndRaw,
      audio_end_ms: audioEnd,
      truncated,
      skip_reason: skipReason,
      ms: atMs,
    };
    this._emit('local', { type: truncated ? 'barge_in.executed' : 'barge_in.truncate_skipped', ...report });
    try { this.onBargeIn(report); } catch { /* ignore listener errors */ }
    this.playing = null;
    return report;
  }

  /** Compat: barge-in manual (UI). Mesma sequência idempotente. */
  cancelResponse() { return this.bargeIn(); }

  _localSilence() {
    const el = this.audioElement;
    if (!el) return;
    try { el.muted = true; } catch { /* ignore */ }
    this._emit('local', { type: 'audio.local_silenced' });
  }

  _localResume() {
    const el = this.audioElement;
    if (!el || !el.muted) return;
    try { el.muted = false; } catch { /* ignore */ }
    try { el.play?.()?.catch?.(() => {}); } catch { /* ignore */ }
    this._emit('local', { type: 'audio.local_resumed' });
  }

  clearAudio() {
    this._sendBargeInCmd({ type: 'output_audio_buffer.clear' });
  }

  /** Tear down everything. Idempotent. */
  close() {
    this.closed = true;
    this.cancelInflightTurn('close');
    this._clearDisconnectTimer();
    this.statusBeforeDisconnect = null;
    try { this.micStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { this.dc?.close(); } catch { /* ignore */ }
    try { this.pc?.close(); } catch { /* ignore */ }
    if (this.audioElement) {
      try { this.audioElement.pause?.(); } catch { /* ignore */ }
      try { this.audioElement.muted = false; } catch { /* ignore */ }
      this.audioElement.onplaying = null;
      this.audioElement.srcObject = null;
    }
    this.micStream = null;
    this.dc = null;
    this.pc = null;
    this.session = null;
    this.turn = null;
    this.sessionId = null;
    this.playing = null;
    this.interruptedResponseIds = new Set();
    this.bargeInEventIds = new Map();
    this.lastBargeInAt = null;
    this.status = RealtimeStatus.IDLE;
    try { this.onStatus(RealtimeStatus.IDLE); } catch { /* ignore */ }
  }
}

export const isRealtimeSupported = () =>
  typeof window !== 'undefined'
  && !!window.RTCPeerConnection
  && !!navigator?.mediaDevices?.getUserMedia;
